"use client"

import React, {
  forwardRef,
  MouseEventHandler,
  useEffect,
  useState,
} from "react"
import * as Toggle from "@radix-ui/react-toggle"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/atoms"

interface IBurgerMenuProps {
  className?: string
  isOpen: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
}

const BurgerMenu = forwardRef<
  React.ElementRef<typeof Toggle.Root>,
  React.ComponentPropsWithoutRef<typeof Toggle.Root> & IBurgerMenuProps
>(({ className, isOpen, onClick, ...props }, ref) => {
  const [pressed, setPressed] = useState(isOpen)

  useEffect(() => {
    setPressed(isOpen)
  }, [isOpen])

  const handlePressedChange = (newPressed: boolean) => {
    setPressed(newPressed)
    // If necessary, inform parent of state change via a callback prop
  }

  return (
    <Toggle.Root
      {...props}
      ref={ref}
      asChild
      onClick={onClick}
      aria-label={`Menu ${pressed ? "Close" : "Open"}`}
      pressed={pressed}
      onPressedChange={handlePressedChange}
      className={cn(
        "inline-flex items-center justify-center text-sm transition-all duration-500 hover:bg-neutral-200 hover:text-current focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=on]:text-current",
        className
      )}
    >
      {pressed ? (
        <Button
          variant="ghost"
          iconLeft="Close"
          className="transition-opacity duration-200"
        />
      ) : (
        <Button
          variant="ghost"
          iconLeft="Menu"
          className="transition-opacity duration-200"
        />
      )}
    </Toggle.Root>
  )
})

BurgerMenu.displayName = "BurgerMenu"

export { BurgerMenu, type IBurgerMenuProps }
